/**
 * Settings → Agent test console. Two tabs:
 *
 *   Text   chat as a customer against the live AI agent (below)
 *   Voice  talk to the voice agent through the browser mic (MicTestCard)
 *
 * Text: chat as a customer against the live AI agent, with the tool calls,
 * resolved prompt, timings and CRM state changes visible next to the transcript.
 *
 * The conversation runs the SAME backend pipeline a real WhatsApp message does
 * — customer resolution, lead resolution, lost-lead routing, the human-takeover
 * gate, the agent's own tools. The only thing missing is delivery. So the
 * intended loop works out of the box: a fresh dummy number is unknown, the
 * agent qualifies intent and creates the lead itself, and the same number is a
 * known customer the next time you use it.
 *
 * Everything written here carries `simulated = true` and can be purged in one
 * click. Reporting already excludes it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { formatInOrgTz } from '../../lib/datetime';
import { Link } from 'react-router-dom';
import { simulatorService, usersService, voiceAgentsService, voiceProvidersService } from '../../lib/services';
import { useToast } from '../../lib/toast';
import { canManageUsers } from '../../lib/roles';
import MicTestCard from './MicTestCard';

const RESERVED_HINT = '+61 499 xxx xxx';

/** ACMA's fictional-mobile range — a number that cannot ring. */
function generateTestNumber() {
  const rand = (n) => String(Math.floor(Math.random() * n)).padStart(3, '0');
  return `+61499${rand(1000)}${rand(1000)}`;
}

function fmtTime(iso) {
  return formatInOrgTz(iso, { hour: '2-digit', minute: '2-digit' }, '');
}

const fmtMs = (ms) => (ms == null ? '—' : ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`);

/* ────────────────────────────── start screen ───────────────────────────── */

function StartScreen({ onStarted }) {
  const { toast } = useToast();
  const [phone, setPhone] = useState('');
  const [look, setLook] = useState(null);      // null = not looked up yet
  const [busy, setBusy] = useState(false);
  const [ack, setAck] = useState(false);

  // Any edit invalidates the previous lookup — otherwise you could acknowledge
  // a real-customer warning for one number and start a session on another.
  const changePhone = (v) => { setPhone(v); setLook(null); setAck(false); };

  const doLookup = async () => {
    if (!phone.trim()) return;
    setBusy(true);
    try {
      setLook(await simulatorService.lookup(phone.trim()));
    } catch (e) {
      toast(e?.response?.data?.message || e?.message || 'Lookup failed', { variant: 'danger' });
    } finally { setBusy(false); }
  };

  const start = async () => {
    setBusy(true);
    try {
      const s = await simulatorService.startSession(phone.trim(), { acknowledgeRealCustomer: ack });
      onStarted(s, look);
    } catch (e) {
      toast(e?.response?.data?.message || e?.message || 'Could not start the session',
        { variant: 'danger' });
    } finally { setBusy(false); }
  };

  const reserved = look?.reservedNumber;

  return (
    <div className="card animate-in" style={{ maxWidth: 620 }}>
      <div className="card-label">START A TEST CONVERSATION</div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '6px 0 14px', lineHeight: 1.6 }}>
        Enter a phone number to chat as. The conversation runs through the same
        pipeline a real WhatsApp message does, so a number the system doesn’t
        know yet will be qualified by the AI and turned into a lead — exactly as
        a cold enquiry would be.
      </div>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span className="card-label">Customer phone number</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={phone}
            onChange={(e) => changePhone(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') doLookup(); }}
            placeholder="+61499123456"
            style={{ flex: 1, fontFamily: 'var(--font-mono, monospace)' }}
          />
          <button type="button" className="btn small"
            onClick={() => changePhone(generateTestNumber())}>
            Generate
          </button>
          <button type="button" className="btn small primary" disabled={busy || !phone.trim()}
            onClick={doLookup}>
            {busy ? 'Checking…' : 'Check number'}
          </button>
        </div>
      </label>

      {look && !reserved && (
        <div className="mono text-xs" style={{ color: 'var(--warning)', marginTop: 8, lineHeight: 1.6 }}>
          ⚠ Outside the reserved test range ({RESERVED_HINT}). It’ll still work —
          SpectraIQ blocks real sends to test customers — but a number that
          can’t ring is one less thing to rely on software for.
        </div>
      )}

      {look && (
        <div className="card" style={{ marginTop: 14, padding: 14, background: 'var(--bg-secondary)' }}>
          {!look.known ? (
            <>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                <span className="pill accent filled">NEW NUMBER</span>
              </div>
              <div className="mono text-xs text-muted" style={{ lineHeight: 1.6 }}>
                No customer on file. The AI will have to ask who they are and
                what they want before it creates a lead — this is the cold-inbound
                path, and the one most worth testing.
              </div>
            </>
          ) : (
            <>
              <div className="flex-between" style={{ marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>
                  {look.firstName || 'Known customer'}
                </span>
                <span className={`pill ${look.realCustomer ? 'danger' : 'success'} filled`}>
                  {look.realCustomer ? 'REAL CUSTOMER' : 'TEST CUSTOMER'}
                </span>
              </div>
              <div className="grid-3" style={{ gap: 8, marginBottom: 8 }}>
                <div><div className="card-label">STAGE</div>
                  <div className="mono text-xs">{look.leadStage || '—'}</div></div>
                <div><div className="card-label">SOURCE</div>
                  <div className="mono text-xs">{look.leadSource || '—'}</div></div>
                <div><div className="card-label">SCORE</div>
                  <div className="mono text-xs">{look.leadScore ?? '—'}</div></div>
              </div>
              {look.recentMessages?.length > 0 && (
                <div className="mono text-xs text-muted" style={{ lineHeight: 1.7 }}>
                  Last message {fmtTime(look.lastInteractionAt)}:
                  {' '}“{(look.recentMessages[look.recentMessages.length - 1]?.body || '').slice(0, 90)}”
                </div>
              )}
            </>
          )}

          {look.realCustomer && (
            <div style={{
              marginTop: 12, padding: 12, borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--danger)', background: 'var(--danger-glow, transparent)',
            }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--danger)', marginBottom: 6 }}>
                This is a real customer, not test data
              </div>
              <div className="mono text-xs" style={{ lineHeight: 1.7, marginBottom: 10 }}>
                Messages will be written to their real timeline. And because the
                simulation guards all key off the customer being test data,
                <b> none of them apply here</b>: the AI could reserve a vehicle
                against their name, fire a write to your DMS, or put this lead on
                a consultant’s board. Your own messages stay purgeable; anything
                the AI creates does not.
              </div>
              <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13 }}>
                <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
                <span>I understand — use this real customer anyway</span>
              </label>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
            <button className="btn small primary" disabled={busy || (look.realCustomer && !ack)}
              onClick={start}>
              {busy ? 'Starting…' : 'Start chat →'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────── debug drawer tabs ────────────────────────── */

function Json({ value }) {
  return (
    <pre className="mono text-xs" style={{
      margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      color: 'var(--text-secondary)', lineHeight: 1.5,
    }}>{JSON.stringify(value, null, 2)}</pre>
  );
}

function ToolsTab({ turn }) {
  const actions = turn?.actionsTaken || [];
  if (!actions.length) {
    return <div className="mono text-xs text-muted">
      The agent answered from the conversation alone — no tools called this turn.
    </div>;
  }
  return (
    <div className="flex-col" style={{ gap: 8 }}>
      {actions.map((a, i) => (
        <details key={i} className="card" style={{ padding: 10 }}>
          <summary style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="mono text-xs" style={{ fontWeight: 600 }}>{a.tool}</span>
            <span className={`pill ${a.blocked ? 'warning' : a.ok ? 'success' : 'danger'} filled`}>
              {a.blocked ? 'BLOCKED' : a.ok ? 'OK' : 'FAILED'}
            </span>
            {a.latency_ms != null && (
              <span className="mono text-xs text-muted">{fmtMs(a.latency_ms)}</span>
            )}
          </summary>
          {a.summary && (
            <div className="mono text-xs" style={{ margin: '8px 0', lineHeight: 1.6 }}>{a.summary}</div>
          )}
          {!a.ok && a.error && (
            <>
              <div className="card-label" style={{ marginTop: 6, color: 'var(--danger)' }}>ERROR</div>
              <pre className="mono text-xs" style={{
                margin: '4px 0 8px', padding: 8, borderRadius: 6,
                background: 'color-mix(in srgb, var(--danger) 8%, transparent)',
                border: '1px solid color-mix(in srgb, var(--danger) 30%, transparent)',
                color: 'var(--danger)', whiteSpace: 'pre-wrap',
                wordBreak: 'break-word', lineHeight: 1.5,
              }}>{a.error}</pre>
            </>
          )}
          <div className="card-label" style={{ marginTop: 6 }}>ARGUMENTS</div>
          <Json value={a.args || {}} />
        </details>
      ))}
      {actions.some((a) => a.blocked) && (
        <div className="mono text-xs" style={{ color: 'var(--warning)', lineHeight: 1.6 }}>
          Blocked tools are still shown on purpose — the agent <i>reaching</i> for
          a real send is behaviour worth knowing about, even though it was refused.
        </div>
      )}
    </div>
  );
}

function PromptTab({ turn }) {
  const d = turn?.debug || {};
  const cfg = d.resolved_config || {};
  const entries = Object.entries(cfg);
  return (
    <div className="flex-col" style={{ gap: 12 }}>
      <div>
        <div className="card-label">RESOLVED CONFIG</div>
        <div className="grid-3" style={{ gap: 8, marginTop: 6 }}>
          {entries.map(([k, v]) => (
            <div key={k}>
              <div className="card-label" style={{ fontSize: 9 }}>{k}</div>
              <div className="mono text-xs" style={{ wordBreak: 'break-word' }}>
                {v === null || v === undefined ? '—' : String(v)}
              </div>
            </div>
          ))}
          {!entries.length && <div className="mono text-xs text-muted">Not reported.</div>}
        </div>
      </div>

      <div>
        <div className="flex-between">
          <span className="card-label">SYSTEM PROMPT</span>
          {d.systemPromptChars != null && (
            <span className="mono text-xs text-muted">{d.systemPromptChars} chars</span>
          )}
        </div>
        {d.systemPromptSha256 && (
          <div className="mono text-xs text-muted" style={{ marginTop: 4, wordBreak: 'break-all' }}>
            sha256 {d.systemPromptSha256.slice(0, 24)}…
          </div>
        )}
        {d.systemPrompt ? (
          <pre className="mono text-xs" style={{
            marginTop: 8, maxHeight: 340, overflow: 'auto', whiteSpace: 'pre-wrap',
            background: 'var(--bg-secondary)', padding: 10,
            borderRadius: 'var(--radius-sm)', lineHeight: 1.5,
          }}>{d.systemPrompt}</pre>
        ) : (
          <div className="mono text-xs text-muted" style={{ marginTop: 8, lineHeight: 1.6 }}>
            {d.systemPromptRedacted
              || 'The prompt was not returned for this turn.'}
          </div>
        )}
      </div>
    </div>
  );
}

function TimingTab({ turn }) {
  const d = turn?.debug || {};
  const rounds = d.rounds || [];
  const usage = d.usage_total || {};
  const max = Math.max(1, ...rounds.map((r) => r.latency_ms || 0));
  const partialUsage = usage.rounds_reporting_usage != null
    && usage.rounds_total != null
    && usage.rounds_reporting_usage < usage.rounds_total;

  return (
    <div className="flex-col" style={{ gap: 12 }}>
      <div className="grid-3" style={{ gap: 8 }}>
        <div><div className="card-label">TOTAL</div>
          <div className="metric-value" style={{ fontSize: 18 }}>{fmtMs(turn?.latencyMs)}</div></div>
        <div><div className="card-label">TOKENS</div>
          <div className="metric-value" style={{ fontSize: 18 }}>
            {usage.total_tokens ? (partialUsage ? '≈' : '') + usage.total_tokens : '—'}
          </div></div>
        <div><div className="card-label">MODEL</div>
          <div className="mono text-xs" style={{ paddingTop: 6 }}>{usage.model || '—'}</div></div>
      </div>

      {usage.failed_over && (
        <div className="mono text-xs" style={{ color: 'var(--warning)', lineHeight: 1.6 }}>
          ⚠ This turn fell over to the backup model. Check the per-round times
          below against LLM_FAILOVER_TIMEOUT_SECONDS — the default budget is 4
          seconds, which a tool round routinely exceeds.
        </div>
      )}
      {partialUsage && (
        <div className="mono text-xs text-muted" style={{ lineHeight: 1.6 }}>
          Only {usage.rounds_reporting_usage} of {usage.rounds_total} rounds
          reported token counts, so the total is a floor, not a full figure.
        </div>
      )}

      <div>
        <div className="card-label" style={{ marginBottom: 6 }}>PER ROUND</div>
        {rounds.length === 0 && <div className="mono text-xs text-muted">Not reported.</div>}
        {rounds.map((r) => (
          <div key={r.index} style={{ marginBottom: 8 }}>
            <div className="flex-between">
              <span className="mono text-xs">
                #{r.index + 1}{r.tool_calls?.length ? ` · ${r.tool_calls.join(', ')}` : ' · reply'}
              </span>
              <span className="mono text-xs text-muted">{fmtMs(r.latency_ms)}</span>
            </div>
            <div style={{ height: 4, background: 'var(--border-color)', borderRadius: 2, marginTop: 3 }}>
              <div style={{
                width: `${Math.round(((r.latency_ms || 0) / max) * 100)}%`,
                height: '100%', background: 'var(--accent-primary)', borderRadius: 2,
              }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StateTab({ turn }) {
  const diff = turn?.stateDiff;
  if (!diff) return <div className="mono text-xs text-muted">No state captured for this turn.</div>;
  const created = Object.entries(diff.createdThisTurn || {});

  const Section = ({ label, before, after, changed }) => (
    <div>
      <div className="card-label">{label}</div>
      {!changed?.length ? (
        <div className="mono text-xs text-muted" style={{ marginTop: 4 }}>Unchanged.</div>
      ) : (
        changed.map((k) => (
          <div key={k} className="mono text-xs" style={{ marginTop: 4, lineHeight: 1.6 }}>
            <span style={{ color: 'var(--text-muted)' }}>{k}: </span>
            <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>
              {JSON.stringify(before?.[k]) ?? 'null'}
            </span>
            {' → '}
            <span style={{ color: 'var(--accent-primary)' }}>{JSON.stringify(after?.[k])}</span>
          </div>
        ))
      )}
    </div>
  );

  return (
    <div className="flex-col" style={{ gap: 12 }}>
      <Section label="LEAD" before={diff.leadBefore} after={diff.leadAfter} changed={diff.leadChanged} />
      <Section label="CUSTOMER" before={diff.customerBefore} after={diff.customerAfter}
        changed={diff.customerChanged} />
      <div>
        <div className="card-label">CREATED THIS TURN</div>
        {created.length === 0 ? (
          <div className="mono text-xs text-muted" style={{ marginTop: 4 }}>Nothing.</div>
        ) : (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
            {created.map(([t, n]) => (
              <span key={t} className="pill accent filled">{n} {t}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const DEBUG_TABS = [
  ['Tools', ToolsTab],
  ['Prompt', PromptTab],
  ['Timing', TimingTab],
  ['State', StateTab],
];

/* ───────────────────────────────── console ─────────────────────────────── */

function Console({ session, lookup, onEnd }) {
  const { toast } = useToast();
  const [messages, setMessages] = useState([]);   // {id, dir, text, ts, meta}
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [lastTurn, setLastTurn] = useState(null);
  const [tab, setTab] = useState(0);
  const [leadId, setLeadId] = useState(session.leadId);
  const [canPurge, setCanPurge] = useState(false);
  const [recording, setRecording] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    usersService.me()
      .then((me) => setCanPurge(canManageUsers(me?.roles || [])))
      .catch(() => setCanPurge(false));
  }, []);

  // Continue a known customer's thread rather than starting from a blank panel:
  // the whole point of a recognised number is that the agent has context, and
  // the tester should see the same context it does.
  useEffect(() => {
    if (!session.wasKnownNumber) return;
    simulatorService.transcript(session.sessionId)
      .then((rows) => setMessages((Array.isArray(rows) ? rows : []).map((r) => ({
        id: r.id,
        dir: r.direction === 'INBOUND' ? 'customer' : 'ai',
        text: r.body,
        ts: r.occurredAt,
        historic: true,
      }))))
      .catch(() => { });
  }, [session.sessionId, session.wasKnownNumber]);

  useEffect(() => {
    // Keep the newest message in view. rAF so it runs after the DOM paints the
    // new row, and smooth so long transcripts don't jump.
    const el = scrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => { el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' }); });
  }, [messages, busy]);

  // Return focus to the input after a turn finishes (and on mount), so the
  // tester can just keep typing instead of clicking back into the box.
  useEffect(() => {
    if (!busy && !recording) inputRef.current?.focus();
  }, [busy, recording]);

  // Render one AI turn (shared by typed and voice-clip messages).
  const applyTurn = useCallback((turn) => {
    setLastTurn(turn);
    if (turn.leadId) setLeadId(turn.leadId);
    if (turn.suppressed) {
      setMessages((m) => [...m, {
        id: `sup-${Date.now()}`, dir: 'system',
        text: suppressedCopy(turn.suppressedReason), ts: new Date().toISOString(),
      }]);
    } else if (turn.replyText) {
      setMessages((m) => [...m, {
        id: turn.outboundInteractionId || `ai-${Date.now()}`,
        dir: 'ai', text: turn.replyText, ts: new Date().toISOString(),
        meta: `${fmtMs(turn.latencyMs)}${turn.actionsTaken?.length ? ` · ${turn.actionsTaken.length} tool${turn.actionsTaken.length > 1 ? 's' : ''}` : ''}`,
      }]);
    } else {
      setMessages((m) => [...m, {
        id: `err-${Date.now()}`, dir: 'system',
        text: turn.error
          ? `The agent didn't answer: ${turn.error}`
          : 'The agent returned an empty reply.',
        ts: new Date().toISOString(),
      }]);
    }
    if (turn.debug?.rate_limited) {
      toast('Rate limit hit (6 messages/minute) — that canned reply is the '
          + 'limiter, not the model. Reset the conversation to clear it.',
        { variant: 'warning' });
    }
  }, [toast]);

  const send = useCallback(async () => {
    const text = draft.trim();
    if (!text || busy) return;
    setDraft('');
    setMessages((m) => [...m, { id: `local-${Date.now()}`, dir: 'customer', text, ts: new Date().toISOString() }]);
    setBusy(true);
    try {
      applyTurn(await simulatorService.sendMessage(session.sessionId, text));
    } catch (e) {
      toast(e?.response?.data?.message || e?.message || 'The turn failed', { variant: 'danger' });
    } finally { setBusy(false); }
  }, [draft, busy, session.sessionId, toast, applyTurn]);

  // ---- Voice clip: record with the mic, transcribe server-side (same STT the
  // real WhatsApp/SMS voice-note path uses), then run the turn as text. ----
  const sendAudio = useCallback(async (blob) => {
    setBusy(true);
    setMessages((m) => [...m, { id: `voice-${Date.now()}`, dir: 'customer',
      text: '🎤 (voice message…)', ts: new Date().toISOString(), pending: true }]);
    try {
      const res = await simulatorService.sendAudio(session.sessionId, blob);
      if (!res?.transcript) {
        setMessages((m) => [...m, { id: `err-${Date.now()}`, dir: 'system',
          text: res?.error || 'Could not transcribe the recording.', ts: new Date().toISOString() }]);
        return;
      }
      // Replace the placeholder with the transcribed text.
      setMessages((m) => m.map((x) => x.pending
        ? { ...x, text: `🎤 ${res.transcript}`, pending: false } : x));
      if (res.turn) applyTurn(res.turn);
    } catch (e) {
      toast(e?.response?.data?.message || e?.message || 'Transcription failed', { variant: 'danger' });
    } finally { setBusy(false); }
  }, [session.sessionId, toast, applyTurn]);

  const toggleRecording = useCallback(async () => {
    if (recording) {
      recorderRef.current?.stop();
      return;
    }
    if (busy) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        if (blob.size > 0) sendAudio(blob);
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (e) {
      toast('Microphone access denied or unavailable. ' + (e?.message || ''), { variant: 'danger' });
    }
  }, [recording, busy, sendAudio, toast]);

  const reset = async () => {
    try {
      await simulatorService.reset(session.sessionId);
      setMessages([]); setLastTurn(null);
      inputRef.current?.focus();
      toast('The agent has forgotten this conversation. The lead is untouched.',
        { variant: 'success' });
    } catch (e) {
      toast(e?.response?.data?.message || 'Reset failed', { variant: 'danger' });
    }
  };

  const purge = async () => {
    if (!window.confirm(
      'Delete every Agent Test Console customer, lead, interaction and booking '
      + 'in this dealership?\n\nReal customer data is not touched.')) return;
    try {
      const r = await simulatorService.purge();
      toast(`Removed ${r.customersDeleted} test customers and ${r.interactionsDeleted} messages.`,
        { variant: 'success' });
      onEnd();
    } catch (e) {
      toast(e?.response?.data?.message || 'Purge failed', { variant: 'danger' });
    }
  };

  const end = async () => {
    try { await simulatorService.close(session.sessionId); } catch { /* closing is best-effort */ }
    onEnd();
  };

  const TabBody = DEBUG_TABS[tab][1];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,420px)',
                  gap: 16, alignItems: 'start' }}>
      <style>{`@keyframes tcBlink { 0%,80%,100% { opacity:0.25 } 40% { opacity:1 } }`}</style>
      {/* ── transcript ── */}
      <div className="card animate-in" style={{
        display: 'flex', flexDirection: 'column',
        // Bound the height so the MESSAGE LIST scrolls internally instead of the
        // whole card growing and pushing the input off-screen.
        height: 'min(72vh, 760px)', minHeight: 480,
      }}>
        <div className="flex-between" style={{ marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
              {lookup?.firstName || 'Test customer'}
              <span className={`pill ${session.realCustomer ? 'danger' : 'warning'} filled`}>
                {session.realCustomer ? 'REAL CUSTOMER' : 'SIMULATED'}
              </span>
            </div>
            <div className="mono text-xs text-muted" style={{ marginTop: 2 }}>
              {session.wasKnownNumber ? 'Known number' : 'New number'} · debounce bypassed
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {leadId && <Link to={`/leads/${leadId}`} className="btn small">Open lead →</Link>}
            <button className="btn small" onClick={reset}>Reset</button>
            <button className="btn small" onClick={end}>End</button>
            {canPurge && <button className="btn small danger" onClick={purge}>Purge test data</button>}
          </div>
        </div>

        <div ref={scrollRef} style={{
          flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column',
          gap: 10, padding: '8px 2px',
        }}>
          {messages.length === 0 && !busy && (
            <div className="text-muted text-xs" style={{ textAlign: 'center', padding: 28, lineHeight: 1.7 }}>
              Say something as the customer.<br />
              {!session.wasKnownNumber
                && 'This number is new, so the agent should ask who you are before it creates a lead.'}
            </div>
          )}
          {messages.map((m) => (
            m.dir === 'system' ? (
              <div key={m.id} className="mono text-xs" style={{
                textAlign: 'center', color: 'var(--warning)', padding: '4px 20px', lineHeight: 1.6,
              }}>{m.text}</div>
            ) : (
              <div key={m.id} style={{
                display: 'flex', justifyContent: m.dir === 'customer' ? 'flex-end' : 'flex-start',
              }}>
                <div className={`chat-bubble ${m.dir === 'customer' ? 'customer' : 'ai'}`}
                  style={{ maxWidth: '82%', opacity: m.historic ? 0.7 : 1 }}>
                  {m.text}
                  <div className="chat-meta">
                    {m.dir === 'customer' ? 'You (as customer)' : 'AI'} · {fmtTime(m.ts)}
                    {m.meta ? ` · ${m.meta}` : ''}
                  </div>
                </div>
              </div>
            )
          ))}
          {busy && (
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <div className="chat-bubble ai" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ display: 'inline-flex', gap: 3 }}>
                  {[0, 1, 2].map((i) => (
                    <span key={i} style={{
                      width: 6, height: 6, borderRadius: '50%', background: 'currentColor',
                      opacity: 0.5, animation: `tcBlink 1.2s ${i * 0.2}s infinite ease-in-out`,
                    }} />
                  ))}
                </span>
                <span className="mono text-xs text-muted">thinking… tool rounds can take up to a minute</span>
              </div>
            </div>
          )}
        </div>

        <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: 10, display: 'flex', gap: 8 }}>
          <input
            ref={inputRef}
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={recording ? 'Recording… press Stop to send' : 'Type as the customer, or 🎤 to speak…'}
            disabled={busy || recording}
            style={{ flex: 1 }}
          />
          <button
            type="button"
            className={`btn small ${recording ? 'danger' : ''}`}
            onClick={toggleRecording}
            disabled={busy && !recording}
            title={recording ? 'Stop and transcribe' : 'Record a voice message (transcribed by your STT provider)'}
          >
            {recording ? '■ Stop' : '🎤'}
          </button>
          <button className="btn small primary" onClick={send} disabled={busy || !draft.trim()}>
            {busy ? 'Sending…' : 'Send'}
          </button>
        </div>
        {recording && (
          <div className="mono text-xs" style={{ color: 'var(--danger)', marginTop: 6 }}>
            ● Recording… press Stop to transcribe and send.
          </div>
        )}
      </div>

      {/* ── debug drawer ── */}
      <div className="card animate-in" style={{
        animationDelay: '0.05s', display: 'flex', flexDirection: 'column',
        height: 'min(72vh, 760px)', minHeight: 480,
      }}>
        <div style={{ display: 'flex', gap: 4, marginBottom: 12, flexWrap: 'wrap' }}>
          {DEBUG_TABS.map(([label], i) => (
            <button key={label} type="button"
              className={`btn small ${i === tab ? 'primary' : ''}`}
              onClick={() => setTab(i)}>
              {label}
            </button>
          ))}
        </div>
        <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {!lastTurn ? (
          <div className="mono text-xs text-muted" style={{ lineHeight: 1.7 }}>
            Send a message to see what the agent did: which tools it called and
            with what arguments, the exact prompt and settings the turn ran on,
            where the time and tokens went, and what changed on the lead.
          </div>
        ) : (
          <TabBody turn={lastTurn} />
        )}
        </div>
      </div>
    </div>
  );
}

/**
 * Plain-language copy for a deliberately-silent turn. These are real production
 * outcomes, not failures, and naming them is the difference between "the AI is
 * broken" and "a consultant owns this conversation".
 */
function suppressedCopy(reason) {
  switch (reason) {
    case 'HUMAN_TAKEOVER':
      return 'The AI stayed quiet: a consultant has taken this lead over. It '
        + 'auto-resumes after 2 unanswered messages and 10 minutes of silence.';
    case 'DEAD_LEAD_ROUTER':
      return 'The AI stayed quiet: the lost-lead router suppressed the reply '
        + '(usually a STOP keyword or an active campaign).';
    case 'EMPTY': return 'Empty message — nothing sent to the agent.';
    case 'NOT_TEXT': return 'Non-text message — the agent only handles text.';
    default: return `The AI did not reply (${reason || 'unknown reason'}).`;
  }
}

/* ───────────────────────────────── panel ───────────────────────────────── */

/**
 * The Voice tab: the browser mic test, against the engine the dealership has
 * SAVED (Settings → Voice agents). Ready when — Standard: provider settings
 * exist; Advanced: at least one active agent, since that is what the call
 * token is minted against.
 */
function VoiceTab() {
  const [ready, setReady] = useState(null);   // null = loading, else { engineMode, enabled }

  useEffect(() => {
    let alive = true;
    Promise.all([
      voiceProvidersService.get().catch(() => null),
      voiceAgentsService.list().catch(() => []),
    ]).then(([view, agents]) => {
      if (!alive) return;
      const engineMode = view?.engine?.mode || 'STANDARD';
      const activeAgents = (agents || []).filter((a) => a.isActive !== false).length;
      setReady({ engineMode, enabled: engineMode === 'ADVANCED' ? activeAgents > 0 : !!view });
    });
    return () => { alive = false; };
  }, []);

  if (!ready) return <div className="text-sm text-muted">Loading voice settings…</div>;
  return <MicTestCard enabled={ready.enabled} engineMode={ready.engineMode} />;
}

const MODES = [
  ['text', 'Text'],
  ['voice', 'Voice'],
];

export default function AgentTestConsole() {
  const [session, setSession] = useState(null);
  const [lookup, setLookup] = useState(null);
  const [mode, setMode] = useState('text');

  return (
    <div className="flex-col" style={{ gap: 16 }}>
      <div className="flex-between" style={{ alignItems: 'flex-start', gap: 12 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Agent test console</div>
          <div className="mono text-xs text-muted" style={{ marginTop: 4, lineHeight: 1.6 }}>
            {mode === 'text'
              ? 'Chat as a customer against the live AI agent. Runs the real inbound pipeline — the only thing it skips is delivery. Everything it creates is tagged as test data, excluded from reporting, and purgeable.'
              : 'Talk to your voice agent through your microphone — the same agent, prompt and tools that answer real calls.'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4 }} role="tablist">
          {MODES.map(([key, label]) => (
            <button key={key} type="button" role="tab" aria-selected={mode === key}
              className={`btn small ${mode === key ? 'primary' : ''}`}
              onClick={() => setMode(key)}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {mode === 'voice' ? (
        <VoiceTab />
      ) : session ? (
        <Console session={session} lookup={lookup}
          onEnd={() => { setSession(null); setLookup(null); }} />
      ) : (
        <StartScreen onStarted={(s, l) => { setSession(s); setLookup(l); }} />
      )}
    </div>
  );
}
